/** SVG path helpers for the kit's charts. Pure functions, pixel coordinates. */

export interface NxPoint {
  x: number;
  y: number;
}

const f = (n: number) => Number(n.toFixed(2));

/**
 * Path through `points`. `smooth` uses monotone cubic interpolation
 * (Fritsch–Carlson): the curve never overshoots a data point, so a smoothed
 * line never invents a peak or a dip the data does not have.
 */
export function linePath(points: readonly NxPoint[], smooth = true): string {
  if (!points.length) return '';
  if (points.length === 1) return `M${f(points[0]!.x)},${f(points[0]!.y)}`;
  if (!smooth || points.length === 2) {
    return points.map((p, i) => `${i ? 'L' : 'M'}${f(p.x)},${f(p.y)}`).join('');
  }
  const n = points.length;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1]!.x - points[i]!.x);
    slope.push(dx[i]! === 0 ? 0 : (points[i + 1]!.y - points[i]!.y) / dx[i]!);
  }
  const tangent: number[] = [slope[0]!];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1]!;
    const b = slope[i]!;
    tangent.push(
      a * b <= 0
        ? 0
        : (3 * (dx[i - 1]! + dx[i]!)) /
            ((2 * dx[i]! + dx[i - 1]!) / a + (dx[i]! + 2 * dx[i - 1]!) / b)
    );
  }
  tangent.push(slope[n - 2]!);
  let d = `M${f(points[0]!.x)},${f(points[0]!.y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p = points[i]!;
    const q = points[i + 1]!;
    const h = dx[i]! / 3;
    d += `C${f(p.x + h)},${f(p.y + tangent[i]! * h)},${f(q.x - h)},${f(q.y - tangent[i + 1]! * h)},${f(q.x)},${f(q.y)}`;
  }
  return d;
}

/** The line path closed down to `baseY` — the area under a line. */
export function areaPath(points: readonly NxPoint[], baseY: number, smooth = true): string {
  if (points.length < 2) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points, smooth)}L${f(last.x)},${f(baseY)}L${f(first.x)},${f(baseY)}Z`;
}

/**
 * A bar from `y0` to `y1` (either order) of width `w` at `x`, with `radius`
 * rounding only the end away from the baseline (`y0`): data ends are rounded,
 * the baseline end stays square.
 */
export function barPath(x: number, y0: number, y1: number, w: number, radius: number): string {
  const top = Math.min(y0, y1);
  const h = Math.abs(y1 - y0);
  if (h <= 0 || w <= 0) return '';
  const r = Math.max(0, Math.min(radius, w / 2, h));
  const up = y1 < y0; // grows upward: round the top
  if (r === 0) return `M${f(x)},${f(top)}h${f(w)}v${f(h)}h${f(-w)}Z`;
  if (up) {
    return (
      `M${f(x)},${f(top + h)}V${f(top + r)}` +
      `Q${f(x)},${f(top)} ${f(x + r)},${f(top)}H${f(x + w - r)}` +
      `Q${f(x + w)},${f(top)} ${f(x + w)},${f(top + r)}V${f(top + h)}Z`
    );
  }
  const bottom = top + h;
  return (
    `M${f(x)},${f(top)}V${f(bottom - r)}` +
    `Q${f(x)},${f(bottom)} ${f(x + r)},${f(bottom)}H${f(x + w - r)}` +
    `Q${f(x + w)},${f(bottom)} ${f(x + w)},${f(bottom - r)}V${f(top)}Z`
  );
}

/** Point on a circle; `angle` in radians, 0 = 3 o'clock, counter-clockwise positive (math). */
export function polar(cx: number, cy: number, r: number, angle: number): NxPoint {
  return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
}

/** Arc along a circle from `start` to `end` angle (radians, math orientation), drawn clockwise on screen when start > end. */
export function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
  const a = polar(cx, cy, r, start);
  const b = polar(cx, cy, r, end);
  const sweep = Math.abs(start - end);
  const large = sweep > Math.PI ? 1 : 0;
  const clockwise = start > end ? 1 : 0;
  return `M${f(a.x)},${f(a.y)}A${f(r)},${f(r)} 0 ${large} ${clockwise} ${f(b.x)},${f(b.y)}`;
}
